import React, { ElementType, useState } from "react";
import {
  Box,
  IconButton,
  Tooltip,
  FormControlLabel,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from "@mui/material";
import MicOffIcon from "@mui/icons-material/MicOff";
import MicIcon from "@mui/icons-material/Mic";
import VideocamOffIcon from "@mui/icons-material/VideocamOff";
import VideocamIcon from "@mui/icons-material/Videocam";
import ScreenShareIcon from "@mui/icons-material/ScreenShare";
import StopScreenShareIcon from "@mui/icons-material/StopScreenShare";
import ChatIcon from "@mui/icons-material/Chat";
import SpeakerNotesOffIcon from "@mui/icons-material/SpeakerNotesOff";
import {
  BatchCapabilityOperation,
  CapabilityAxis,
} from "../../api/video_platform";

/** Axes the host can batch-toggle for the whole meeting. */
type BatchAxis =
  | CapabilityAxis.Audio
  | CapabilityAxis.Video
  | CapabilityAxis.Content
  | CapabilityAxis.Chat;

interface AxisConfig {
  label: string;
  lockOp: BatchCapabilityOperation;
  unlockOp: BatchCapabilityOperation;
  IconUnlocked: ElementType;
  IconLocked: ElementType;
}

/** Single source of truth for per-axis batch wiring (label, ops, icons). */
const AXIS_CONFIG: Record<BatchAxis, AxisConfig> = {
  [CapabilityAxis.Audio]: {
    label: "Audio",
    lockOp: BatchCapabilityOperation.MuteAll,
    unlockOp: BatchCapabilityOperation.UnmuteAll,
    IconUnlocked: MicIcon,
    IconLocked: MicOffIcon,
  },
  [CapabilityAxis.Video]: {
    label: "Video",
    lockOp: BatchCapabilityOperation.DisableVideoAll,
    unlockOp: BatchCapabilityOperation.EnableVideoAll,
    IconUnlocked: VideocamIcon,
    IconLocked: VideocamOffIcon,
  },
  [CapabilityAxis.Content]: {
    label: "Screen Share",
    lockOp: BatchCapabilityOperation.DisableContentAll,
    unlockOp: BatchCapabilityOperation.EnableContentAll,
    IconUnlocked: ScreenShareIcon,
    IconLocked: StopScreenShareIcon,
  },
  [CapabilityAxis.Chat]: {
    label: "Chat",
    lockOp: BatchCapabilityOperation.MuteChatAll,
    unlockOp: BatchCapabilityOperation.UnmuteChatAll,
    IconUnlocked: ChatIcon,
    IconLocked: SpeakerNotesOffIcon,
  },
};

const AXES: BatchAxis[] = [
  CapabilityAxis.Audio,
  CapabilityAxis.Video,
  CapabilityAxis.Content,
  CapabilityAxis.Chat,
];

interface BatchCapabilityControlsProps {
  meetingId: string;
  onBatchUpdate: (
    operation: BatchCapabilityOperation,
    excludeHosts: boolean,
    lock: boolean
  ) => Promise<void>;
  disabled?: boolean;
  /** Per-axis current global lock state (true = currently locked for all). */
  isAudioLocked?: boolean;
  isVideoLocked?: boolean;
  isContentLocked?: boolean;
  isChatLocked?: boolean;
}

interface PendingAction {
  axis: BatchAxis;
  operation: BatchCapabilityOperation;
  willLock: boolean;
}

const BatchCapabilityControls: React.FC<BatchCapabilityControlsProps> = ({
  onBatchUpdate,
  disabled = false,
  isAudioLocked = false,
  isVideoLocked = false,
  isContentLocked = false,
  isChatLocked = false,
}) => {
  const [pending, setPending] = useState<PendingAction | null>(null);
  const [excludeHosts, setExcludeHosts] = useState(true);
  const [loading, setLoading] = useState(false);

  const isLocked = (axis: BatchAxis): boolean => {
    switch (axis) {
      case CapabilityAxis.Audio:
        return isAudioLocked;
      case CapabilityAxis.Video:
        return isVideoLocked;
      case CapabilityAxis.Content:
        return isContentLocked;
      case CapabilityAxis.Chat:
        return isChatLocked;
    }
  };

  const handleAxisClick = (axis: BatchAxis) => {
    const cfg = AXIS_CONFIG[axis];
    const willLock = !isLocked(axis);
    setPending({
      axis,
      operation: willLock ? cfg.lockOp : cfg.unlockOp,
      willLock,
    });
  };

  const handleConfirm = async () => {
    if (!pending) return;
    setLoading(true);
    try {
      await onBatchUpdate(pending.operation, excludeHosts, true);
    } catch (error) {
      // Surface to the console; the dialog closes either way so the host
      // can retry. Toast/snackbar handling lives in the parent dialog.
      console.error("Failed to apply batch capability change", error);
    } finally {
      setLoading(false);
      setPending(null);
    }
  };

  const handleCancel = () => setPending(null);

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
      {AXES.map((axis) => {
        const cfg = AXIS_CONFIG[axis];
        const locked = isLocked(axis);
        const Icon = locked ? cfg.IconLocked : cfg.IconUnlocked;
        return (
          <Tooltip
            key={axis}
            title={
              locked
                ? `Unlock ${cfg.label} for All`
                : `Lock ${cfg.label} for All`
            }
          >
            <IconButton
              size="small"
              onClick={() => handleAxisClick(axis)}
              disabled={disabled || loading}
              color={locked ? "error" : "default"}
            >
              <Icon fontSize="small" />
            </IconButton>
          </Tooltip>
        );
      })}

      <Dialog open={!!pending} onClose={handleCancel}>
        <DialogTitle>
          {pending?.willLock ? "Lock" : "Unlock"}{" "}
          {pending && AXIS_CONFIG[pending.axis].label} for All
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {pending?.willLock
              ? `This will disable ${AXIS_CONFIG[pending.axis].label.toLowerCase()} for all attendees and prevent them from enabling it.`
              : `This will allow all attendees to control their own ${pending ? AXIS_CONFIG[pending.axis].label.toLowerCase() : ""}.`}
          </DialogContentText>
          {/* Exclude-hosts only applies when locking — unlock hits everyone. */}
          {pending?.willLock && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={excludeHosts}
                  onChange={(e) => setExcludeHosts(e.target.checked)}
                  size="small"
                />
              }
              label="Exclude hosts from this action"
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            color={pending?.willLock ? "error" : "primary"}
            variant="contained"
            disabled={loading}
          >
            {loading ? "Updating..." : pending?.willLock ? "Lock" : "Unlock"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BatchCapabilityControls;
